import React, { ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'

import { Box } from '../box'
import { Text } from '../text'
import type { TextSize, TextTone } from '../text/variants'

export type LabeledTextSize = 'small' | 'medium' | 'large'

export type LabeledTextProps = {
    label: ReactNode
    size?: LabeledTextSize
    reversed?: boolean
    labelTone?: TextTone
    valueTone?: TextTone
    className?: string
    children?: ReactNode
}

const sizes: Record<LabeledTextSize, { top: TextSize; bottom: TextSize }> = {
    small: { top: 'caption', bottom: 'body' },
    // Figma "FlightDetail" pair: P2 muted label over P2 default value.
    medium: { top: 'body', bottom: 'body' },
    large: { top: 'body', bottom: 'body-lg' },
}

export const LabeledText: React.FC<LabeledTextProps> = ({
    label,
    size = 'large',
    reversed = false,
    labelTone = 'muted',
    valueTone = 'default',
    className,
    children,
}) => {
    const { top, bottom } = sizes[size]
    const labelSize = reversed ? bottom : top
    const valueSize = reversed ? top : bottom
    const isTextValue = typeof children === 'string' || typeof children === 'number'

    return (
        <Box className={twMerge('gap-0.5', className)}>
            <Text size={labelSize} tone={labelTone}>
                {label}
            </Text>
            {children !== undefined &&
                children !== null &&
                (isTextValue ? (
                    <Text size={valueSize} tone={valueTone}>
                        {children}
                    </Text>
                ) : (
                    <Box>{children}</Box>
                ))}
        </Box>
    )
}

LabeledText.displayName = 'LabeledText'
